// models to loading from Server
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { ArticleAvailabilityDataModel, ArticleTagDataModel, ItemTagViewModel, ProductDataModel4 } from "../shared/models/product.model";
import { IObjectObserver } from "./IObjectObserver";
import { Injectable, OnDestroy } from "@angular/core";
import { ObjectObserverFactory } from "./objectObserverFactory";
import { ContentType, ItemTagStatusType, YourAPPProductType } from "../shared/contentTypes";
import { CoreLogService } from "./log.service";
import { MenuSettingsDataModel2, MenuSettingsDMKey, Ordertype, WebshopSettingsDataModel2, WebshopSettingsDMKey } from "../shared/models";
import { Observable, Subject, Subscription, interval } from "rxjs";
import { ProductObjectObserverData } from "./objectObserver.service";
import { ProductDetailsHelperService } from "./product_details_helper.service";
import * as moment from "moment";
import { CoreCOMService } from "./com.service";
import { environment } from "../../../environments/environment";

// The Duration between getting new availibility data in ms;
const ARTICLEAVAILIBILITY_UPDATERATE = 5 * 60 * 1000;

/** Product Service*/
@Injectable({
  providedIn: "root" // todo remove
})
export class ProductService implements OnDestroy {
  // Menu Settings
  private _menuSettingsObsServ: IObjectObserver<MenuSettingsDataModel2>;
  private _menuSettingsChanged: Subject<void> = new Subject();

  // Webshop Settings
  private _webshopOrdertypes: Ordertype[];
  private _webshopSettingsObsServ: IObjectObserver<WebshopSettingsDataModel2>;

  // Article
  private _menuSettingLocations: number[];
  private _productObsServDic: { [key: number]: { [sortiment: string]: IObjectObserver<ProductDataModel4> } } = {};
  private _productChanged: Subject<void> = new Subject();

  // Article Availability
  private _availabiltyCountMap: { [locationid: number]: { [productid: number]: number } };
  private _availabiltyBlacklistMap: { [locationid: number]: { [productid: number]: boolean } };
  private _articleAvailabilityObsServ: IObjectObserver<ArticleAvailabilityDataModel>;
  private _observeChangesSubscription: Subscription;
  private _articleAvailabilityChanged: Subject<void> = new Subject();
  private _updateSubscription: Subscription;

  private _initialized: boolean = false;

  constructor(private _coreLogService: CoreLogService
    , private _objObsInjectHelper: CircularInjectionHelper) {
    const me = this;
    if (environment.producttype == YourAPPProductType.sot) {
      return; // break here, sot dont need WebShopService
    }
    me._menuSettingsObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<MenuSettingsDataModel2>(ContentType.MenuSettings, me._objObsInjectHelper);
    me._menuSettingsObsServ.ObserveChanges.subscribe(me.onMenuSettingsChanged.bind(me));
    me._webshopSettingsObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<WebshopSettingsDataModel2>(ContentType.WebshopSettings, me._objObsInjectHelper);
    me._webshopSettingsObsServ.ObserveChanges.subscribe(me.onMenuSettingsChanged.bind(me));
    me.onMenuSettingsChanged();
    me._articleAvailabilityObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<ArticleAvailabilityDataModel>(ContentType.ArticleAvailabililty, me._objObsInjectHelper);
    me._observeChangesSubscription = me._articleAvailabilityObsServ.ObserveChanges.subscribe(me.onArticleAvailbilityUpdate.bind(me));
    me._updateSubscription = interval(ARTICLEAVAILIBILITY_UPDATERATE).subscribe(() => {
      if (me._objObsInjectHelper.coreCOMService.environment.networkInformation.isOnline) {
        me._articleAvailabilityObsServ.RefreshObjectsFromServer();
      }
    });
    me.onArticleAvailbilityUpdate();
    me._initialized = true;
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._observeChangesSubscription) {
      me._observeChangesSubscription.unsubscribe();
    }
    if (me._updateSubscription) {
      me._updateSubscription.unsubscribe();
    }
  }

  private GetProductObsServ(locationId: number, sortiment: string): IObjectObserver<ProductDataModel4> {
    const me = this;
    if (!me._productObsServDic[locationId]) {
      me._productObsServDic[locationId] = {};
    }
    if (!me._productObsServDic[locationId][sortiment]) {
      const data = new ProductObjectObserverData(locationId, sortiment);
      const _productObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<ProductDataModel4>(ContentType.sub_Product, me._objObsInjectHelper, data);
      // durch die Änderung des Nachladens (bei zugriff abgleich der ChangeAt) ist das nicht mehr notwendig (auskommentiert als Beispiel wenn wieder notwendig werden sollte)
      //ProductDetailsHelperService.DownloadFaildProductDetails(_productObsServ, me._objObsInjectHelper)
      //  .then() // ignore response
      //  .catch((error) => {
      //    me._coreLogService.error("ProductService.GetProductObsServ", "ProductDetailsHelperService.DownloadProductDetails", error);
      //  });
      // event weiterreichen
      _productObsServ.ObserveChanges.subscribe(() => { me._productChanged.next(); });
      me._productObsServDic[locationId][sortiment] = _productObsServ;
    }
    return me._productObsServDic[locationId][sortiment];
  }

  /** GetProductLastSyncAt */
  public GetProductLastSyncAt(locationId: number, sortiment: string): Promise<moment.Moment> {
    return this.GetProductObsServ(locationId, sortiment).GetLastSyncAt();
  }

  /** GetProduct */
  public GetProduct(locationId: number, sortiment: string, key: number | string): Promise<ProductDataModel4> {
    return this.GetProductObsServ(locationId, sortiment).GetObject(key);
  }

  /** GetProductsByFilter */
  public GetProductsByFilter(locationId: number, sortiment: string, filter: (obj: ProductDataModel4) => boolean): Promise<ProductDataModel4[]> {
    const me = this;
    return this.GetProductObsServ(locationId, sortiment).GetObjectsByFilter(filter);
  }

  /** GetAllProducts */
  public GetAllProducts(locationId: number, sortiment: string): Promise<ProductDataModel4[]> {
    return this.GetProductObsServ(locationId, sortiment).GetAllObjects();
  }

  public get ProductChanges(): Observable<void> {
    return this._productChanged;
  }

  public get ArticleAvailabilityChanged(): Observable<void> {
    return this._articleAvailabilityChanged;
  }

  /** GetMenuSettings */
  public GetMenuSettings(): Promise<MenuSettingsDataModel2> {
    return this._menuSettingsObsServ.GetObject(MenuSettingsDMKey);
  }

  public get MenuSettingsChangedChanges(): Observable<void> {
    return this._menuSettingsChanged;
  }

  public updateArticleAvailabilty() {
  }

  private onMenuSettingsChanged() {
    const me = this;
    Promise.all([
      me._menuSettingsObsServ.GetObject(MenuSettingsDMKey),
      me._webshopSettingsObsServ.GetObject(WebshopSettingsDMKey)
    ]).then(([menuSettings, webshopSettings]) => {
      // initial run
      if (!me._initialized && !me._webshopOrdertypes) {
        me._webshopOrdertypes = webshopSettings.ordertypes;
        me._menuSettingLocations = menuSettings.locations;
      }
      let hasChanges = false;

      if (me._webshopOrdertypes !== webshopSettings.ordertypes) {
        Object.entries(me._productObsServDic).forEach(([location, sortimentMap]) => {
          Object.entries(sortimentMap).forEach(([sortiment, Observer]) => {
            ObjectObserverFactory.ReleaseObjectObserverByObserver(Observer);
            delete sortimentMap[sortiment];
          });
          delete me._productObsServDic[location];
        });
        hasChanges = true;
      } else if (me._menuSettingLocations === menuSettings.locations) {
        // bei neuen locations als änderung mitteilen
        if (me._menuSettingLocations < menuSettings.locations) {
          hasChanges = true;
        }
        // entfernte locations freigeben
        me._menuSettingLocations.forEach(location => {
          if ((!menuSettings.locations.includes(location)) && me._productObsServDic[location]) {
            const sortimentMap = me._productObsServDic[location];
            Object.entries(sortimentMap).forEach(([sortiment, Observer]) => {
              ObjectObserverFactory.ReleaseObjectObserverByObserver(Observer);
              delete sortimentMap[sortiment];
            });
            delete me._productObsServDic[location];
            hasChanges = true;
          }
        });
      }
      // store new data
      me._webshopOrdertypes = webshopSettings.ordertypes;
      me._menuSettingLocations = menuSettings.locations;

      if (me._initialized) {
        me._menuSettingsChanged.next();
        if (hasChanges) {
          me._productChanged.next();
        }
      }
    }).catch(e => {
      me._coreLogService.error("WebShopService.onArticleAvailbilityUpdate", "_articleAvailabilityObsServ.GetAllObjects()", e);
      me._menuSettingsChanged.next();
      me._productChanged.next(); // auch producte, da unsicher, ob sie betroffen waren
    });
  }

  private onArticleAvailbilityUpdate() {
    const me = this;
    me._articleAvailabilityObsServ.GetAllObjects().then(aa => {
      me._availabiltyCountMap = {};
      me._availabiltyBlacklistMap = {};
      aa.forEach(locationAvailabilty => {
        const productMap = {};
        const aaIDs = locationAvailabilty.product_ids.split("|");
        const aaCounts = locationAvailabilty.product_ids_count.split("|");
        if (aaIDs.length === aaCounts.length) {
          aaIDs.forEach((id, index) => productMap[id] = parseInt(aaCounts[index], 10));
        } else {
          me._coreLogService.error("WebShopService.onArticleAvailbilityUpdate", "aaIDs and aaCounts should be the same length");
        }
        const aaBlacklistIDs = locationAvailabilty.product_ids_blacklist.split("|");
        const blacklistMap = aaBlacklistIDs.reduce((a, b) => { a[b] = true; return a; }, {});
        me._availabiltyCountMap[locationAvailabilty.location] = productMap;
        me._availabiltyBlacklistMap[locationAvailabilty.location] = blacklistMap;
      });
      if (me._initialized) {
        me._articleAvailabilityChanged.next();
      }
    }).catch(e => {
      me._coreLogService.error("WebShopService.onArticleAvailbilityUpdate", "_articleAvailabilityObsServ.GetAllObjects()", e);
      me._articleAvailabilityChanged.next();
    });
  }

  // -1 means it is not an availability item
  // product_id aus der Kasse und nicht die ID des Artikels!
  public getProductAvailabilityCount(locationID: number, productID: number): number {
    const locMap = this._availabiltyCountMap[locationID];
    if (locMap) {
      const productMap = locMap[productID];
      if (productMap != null) {
        return productMap;
      }
    }
    return -1;
  }

  // product_id aus der Kasse und nicht die ID des Artikels!
  public isProductAvailabilityBlacklisted(locationID: number, productID: number): boolean {
    const locMap = this._availabiltyBlacklistMap[locationID];
    if (locMap) {
      const productMap = locMap[productID];
      if (productMap != null) {
        return productMap;
      }
    }
    return false;
  }

  public RefreshLocationProductData(locationID: number, sortiment: string): Promise<void> {
    return this.GetProductObsServ(locationID, sortiment).RefreshObjectsFromServer();
  }

  /** bereinigt den localen Speicher */
  public DeleteLocalLocationProductData(locationID: number, sortiment: string): Promise<void> {
    return this.GetProductObsServ(locationID, sortiment).DeleteLocalStoredData(true);
  }
}
